<template>
    <div ref="rootElement" class="flood-zone-layer" style="display: none;"></div>
</template>

<script lang="ts">
/**
 * Flood overlays of a map, read from the application's own flood data layer: the designated flood
 * areas of the 5-, 20-, 100- and 500-year floods, the active zone, the hazard categories of the
 * flood hazard map and the extents of recorded floods. Every scenario is an overlay of its own in
 * the layers control, under a "Povodňová mapa" header. The pieces of the switched-on scenarios are
 * loaded for the viewport from the street zoom up, in one request; a click on the map asks what
 * every layer says about the place.
 */
import {
    ATTRIBUTION,
    DISCLAIMER,
    escapeHtml,
    FLOOD_ZONE_LABELS,
    FLOOD_ZONES_UNAVAILABLE,
    FloodLayerInfo,
    FloodRiskProfile,
    FLOODING_RISKS,
    floodZonesReaching,
    formatMetres,
    FloodZoneArea,
    HAZARD_CATEGORIES,
    hazardLabel,
    historicalFloods,
    MIN_ZOOM_BY_SCENARIO,
    SCENARIO_ORDER,
    SCENARIOS,
} from '@/components/flood/floodProfile';
import {loadFloodProfile} from '@/model/flood/floodProfile';
import {acquireLeafletMap, AcquiredMap} from '@/shared/leafletMapRegistry';
import {registerMinZoom, unregisterMinZooms} from '@/shared/layersControlMinZoom';
import L from 'leaflet';
import {defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

const SECTION_TITLE = 'Povodňová mapa';
const HEADER_CLASS = 'flood-zone-header';
const PANE_NAME = 'floodZone';
const PANE_Z_INDEX = '250';
// The server serves the zones in bands: generalized and further simplified at 11 and 12,
// generalized at 13, simplified to about a pixel of the zoom (small pieces left out) at 14, 15
// and 16, exact from 17 up. The band is what the loaded data depend on.
const LAST_BAND_ZOOM = 17;
const BOUNDS_PADDING = 0.25;
const FOCUS_MARKER_STYLE: L.CircleMarkerOptions = {radius: 7, weight: 3, color: '#ffffff', fillColor: '#174c6c', fillOpacity: 1, interactive: false};

export default defineComponent({
    props: {
        loadAreasUrl: {type: String, required: true},
        layers: {type: Array as PropType<FloodLayerInfo[]>, default: () => []},
        // Opened on a place (from the flood profile of an estate): the place is marked and its
        // flood risk opened once the map is there.
        focus: {type: Object as PropType<Coordinates | null>, default: null},
        // The overlays switched on from the start.
        initialScenarios: {type: Array as PropType<string[]>, default: () => []},
    },
    setup(props) {
        const rootElement = ref<HTMLElement | null>(null);

        let map: L.Map | null = null;
        let layersControl: L.Control.Layers | null = null;
        let originalUpdate: (() => unknown) | null = null;
        const overlays: Map<string, L.GeoJSON> = new Map();
        const overlayIds: Set<number> = new Set();
        let legend: L.Control | null = null;
        let zoomHint: L.Control | null = null;
        let abortController: AbortController | null = null;
        let loadedBounds: L.LatLngBounds | null = null;
        let loadedBand: number | null = null;
        let loadedScenarios = '';
        let cleanupMapListener: (() => void) | null = null;
        let focusMarker: L.CircleMarker | null = null;
        // The popup opened last by any overlay of the map, and the clicks asked about so far: the
        // answer to a click comes late, and by then another overlay may have claimed the click.
        let lastOpenedPopup: L.Popup | null = null;
        let clickSequence = 0;

        const versions: Record<string, string> = {};
        for (const layer of props.layers) {
            versions[layer.scenario] = layer.sourceVersion;
        }
        const availableScenarios = SCENARIO_ORDER.filter((scenario) => scenario in versions);

        function overlayLabel(scenario: string): string {
            return scenario === 'hazard' ? hazardLabel(versions.hazard ?? null) : SCENARIOS[scenario].label;
        }

        function areaStyle(area: FloodZoneArea): L.PathOptions {
            const style = SCENARIOS[area.scenario];
            const color = area.scenario === 'hazard' && area.hazardCategory !== null
                ? HAZARD_CATEGORIES[area.hazardCategory]?.color ?? style.color
                : style.color;

            // Fill only: the pieces are the zones cut up for the database, and a stroke would draw
            // the cuts as a grid inside the zone.
            return {
                pane: PANE_NAME,
                stroke: false,
                fillColor: color,
                fillOpacity: style.fillOpacity,
                interactive: false,
            };
        }

        function enabledScenarios(): string[] {
            if (!map) {
                return [];
            }
            const current = map;
            return availableScenarios.filter((scenario) => {
                const overlay = overlays.get(scenario);
                return overlay !== undefined && current.hasLayer(overlay);
            });
        }

        // The switched-on overlays the map is zoomed in enough for.
        function shownScenarios(): string[] {
            const zoom = map?.getZoom() ?? 0;
            return enabledScenarios().filter((scenario) => zoom >= MIN_ZOOM_BY_SCENARIO[scenario]);
        }

        // The switched-on overlays waiting for a closer zoom.
        function hiddenScenarios(): string[] {
            const zoom = map?.getZoom() ?? 0;
            return enabledScenarios().filter((scenario) => zoom < MIN_ZOOM_BY_SCENARIO[scenario]);
        }

        function hasSize(current: L.Map): boolean {
            const size = current.getSize();
            return size.x > 0 && size.y > 0;
        }

        // In a dialog the map exists before the dialog is shown, with no size at all; a popup
        // opened then is placed (and the map panned to it) over an empty container and ends up
        // away from its place. The work waits for the first real size.
        function whenSized(current: L.Map, work: () => void): void {
            if (hasSize(current)) {
                work();
                return;
            }

            const onResize = (): void => {
                if (hasSize(current)) {
                    current.off('resize', onResize);
                    work();
                }
            };
            current.on('resize', onResize);
        }

        async function load(): Promise<void> {
            if (!map || !hasSize(map)) {
                return;
            }

            const scenarios = shownScenarios();
            const zoom = map.getZoom();

            for (const scenario of hiddenScenarios()) {
                overlays.get(scenario)?.clearLayers();
            }

            if (scenarios.length === 0) {
                loadedBounds = null;
                loadedBand = null;
                loadedScenarios = '';
                return;
            }

            const bounds = map.getBounds();
            const key = scenarios.join(',');
            // Fractional zooms (the map snaps to quarters) fall into the band of their whole part.
            const band = Math.min(LAST_BAND_ZOOM, Math.floor(zoom));

            if (loadedScenarios === key && loadedBand === band && loadedBounds && loadedBounds.contains(bounds)) {
                return;
            }

            const paddedBounds = bounds.pad(BOUNDS_PADDING);
            abortController?.abort();
            const controller = new AbortController();
            abortController = controller;

            try {
                const response = await fetch(props.loadAreasUrl, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        boundingBox: {
                            southWest: {longitude: paddedBounds.getWest(), latitude: paddedBounds.getSouth()},
                            northEast: {longitude: paddedBounds.getEast(), latitude: paddedBounds.getNorth()},
                        },
                        zoom: band,
                        scenarios,
                    }),
                    signal: controller.signal,
                });

                if (!response.ok) {
                    return;
                }

                const areas: FloodZoneArea[] = await response.json();
                const byScenario: Map<string, GeoJSON.Feature[]> = new Map();

                for (const area of areas) {
                    const features = byScenario.get(area.scenario) ?? [];
                    features.push({type: 'Feature', geometry: area.geometry, properties: area});
                    byScenario.set(area.scenario, features);
                }

                for (const [scenario, overlay] of overlays) {
                    overlay.clearLayers();
                    const features = byScenario.get(scenario);
                    if (features && scenarios.includes(scenario)) {
                        const collection: GeoJSON.FeatureCollection = {type: 'FeatureCollection', features};
                        overlay.addData(collection);
                    }
                }

                loadedBounds = paddedBounds;
                loadedBand = band;
                loadedScenarios = key;
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    loadedBounds = null;
                }
            }
        }

        function legendContent(): string {
            const rows: string[] = [];

            for (const scenario of shownScenarios()) {
                if (scenario === 'hazard') {
                    rows.push(`<div class="flood-legend__title">${escapeHtml(hazardLabel(versions.hazard ?? null))}</div>`);
                    for (const category of [4, 3, 2, 1]) {
                        rows.push(`<div class="flood-legend__item"><span class="flood-legend__swatch" style="background:${HAZARD_CATEGORIES[category].color}"></span>${HAZARD_CATEGORIES[category].label}</div>`);
                    }
                    continue;
                }

                const style = SCENARIOS[scenario];
                rows.push(`<div class="flood-legend__item"><span class="flood-legend__swatch" style="background:${style.color};opacity:${style.fillOpacity + 0.3}"></span>${escapeHtml(style.label)}</div>`);
            }

            return `${rows.join('')}<div class="flood-legend__note">${escapeHtml(ATTRIBUTION)}</div>`;
        }

        // Bottom right, above the attribution: the bottom-left corner holds the zoom level label of the map.
        function buildLegend(): L.Control {
            const control = new L.Control({position: 'bottomright'});
            control.onAdd = () => {
                const element = L.DomUtil.create('div', 'flood-legend');
                L.DomEvent.disableClickPropagation(element);
                return element;
            };
            return control;
        }

        // Bottom right like the legend (they never show together): the bottom-left corner holds the
        // zoom level label of the transactions map, which covered the hint.
        function buildZoomHint(): L.Control {
            const control = new L.Control({position: 'bottomright'});
            control.onAdd = () => L.DomUtil.create('div', 'flood-zoom-hint');
            return control;
        }

        // Names the overlays waiting for a closer zoom, or the whole flood map when none shows.
        function zoomHintText(): string {
            const hidden = hiddenScenarios();
            if (shownScenarios().length === 0) {
                return 'Pro zobrazení povodňové mapy přibližte mapu';
            }
            // The names quoted, so that they keep their own form after "vrstvy"/"vrstev".
            const names = hidden.map((scenario) => `„${SCENARIOS[scenario].label}“`).join(', ');
            return `Pro zobrazení ${hidden.length > 1 ? 'vrstev' : 'vrstvy'} ${names} přibližte mapu`;
        }

        function updateControls(): void {
            if (!map || !legend || !zoomHint) {
                return;
            }

            // Adding a control that is on the map already just moves it, so this is safe to repeat.
            if (shownScenarios().length > 0) {
                legend.addTo(map);
                const element = legend.getContainer();
                if (element) {
                    element.innerHTML = legendContent();
                }
            } else {
                legend.remove();
            }

            if (hiddenScenarios().length > 0) {
                zoomHint.addTo(map);
                const element = zoomHint.getContainer();
                if (element) {
                    element.textContent = zoomHintText();
                }
            } else {
                zoomHint.remove();
            }
        }

        // What the designated flood areas say about the place, or that they are not imported yet.
        function floodZoneLines(profile: FloodRiskProfile): string[] {
            if (!profile.floodZonesAvailable) {
                return [`<strong>${escapeHtml(FLOOD_ZONES_UNAVAILABLE)}</strong>`];
            }

            const lines: string[] = [];
            const floodZones = floodZonesReaching(profile);

            if (floodZones.length > 0) {
                const stream = profile.streamName ? ` (${escapeHtml(profile.streamName)})` : '';
                lines.push(`<strong>Záplavové území:</strong> ${floodZones.map((scenario) => FLOOD_ZONE_LABELS[scenario]).join(', ')} voda${stream}`);
            } else {
                lines.push('<strong>Mimo stanovená záplavová území</strong>');
                if (profile.distanceToQ100Metres !== null) {
                    lines.push(`Nejbližší hranice 100leté vody: ${formatMetres(profile.distanceToQ100Metres)}`);
                }
            }

            if (profile.scenarios.includes('active_zone')) {
                lines.push('<strong>Aktivní zóna záplavového území</strong> (zákaz staveb podle vodního zákona)');
            }

            const floodingRisk = profile.floodingRisk ?? '';
            const risk = FLOODING_RISKS[floodingRisk];
            lines.push(`Nebezpečí povodně: ${risk ? `${risk.label} (${risk.description})` : escapeHtml(floodingRisk)}`);

            if (profile.historicalFloodPeriods.length > 0) {
                lines.push(`Zaplaveno při povodni: ${historicalFloods(profile).map(escapeHtml).join(', ')}`);
            }

            return lines;
        }

        function profileContent(profile: FloodRiskProfile): string {
            const lines = floodZoneLines(profile);

            const version = profile.hazardMapVersion ? ` (stav ${escapeHtml(profile.hazardMapVersion)})` : '';
            if (!profile.hazardMapAvailable) {
                lines.push('Mapa povodňového ohrožení není pro tuto lokalitu zpracována.');
            } else if (profile.hazardCategory !== null && HAZARD_CATEGORIES[profile.hazardCategory]) {
                const category = HAZARD_CATEGORIES[profile.hazardCategory];
                lines.push(`<strong>Povodňové ohrožení:</strong> <span class="flood-legend__swatch" style="background:${category.color}"></span>${category.label}${version}`);
            } else {
                lines.push(`Povodňové ohrožení: mimo zaplavované území${version}`);
            }

            return `<div class="flood-popup__title">Povodňové riziko</div>`
                + lines.map((line) => `<div>${line}</div>`).join('')
                + `<div class="flood-popup__note">${escapeHtml(DISCLAIMER)} ${escapeHtml(ATTRIBUTION)}</div>`;
        }

        // Asks what every layer says about the place and opens it in a popup there, unless the
        // answer is no longer wanted by the time it arrives.
        async function showPlace(place: L.LatLng, isStillWanted: () => boolean = () => true): Promise<void> {
            if (!map) {
                return;
            }

            const current = map;

            try {
                const profile = await loadFloodProfile(place.lat, place.lng);

                if (!isStillWanted()) {
                    return;
                }

                // The width is fixed (see the styles): opened in a dialog before it is shown, the
                // popup would otherwise measure an empty container and come out 50 px wide.
                L.popup({className: 'flood-popup', minWidth: 300, maxWidth: 300})
                    .setLatLng(place)
                    .setContent(profileContent(profile))
                    .openOn(current);
            } catch {
                // A failed lookup leaves the map as it is.
            }
        }

        function onMapClick(event: L.LeafletMouseEvent): void {
            if (!map || shownScenarios().length === 0) {
                return;
            }

            // A parcel, a building or a zoning area clicked opens its own popup right away; the
            // object clicked is what the click is about, so the flood popup gives way to it. A later
            // click makes the answer to this one stale.
            const sequence = ++clickSequence;
            const popupBeforeClick = lastOpenedPopup;
            void showPlace(event.latlng, () => sequence === clickSequence && lastOpenedPopup === popupBeforeClick);
        }

        function onPopupOpen(event: L.PopupEvent): void {
            lastOpenedPopup = event.popup;
        }

        function onMapChange(): void {
            updateControls();
            void load();
        }

        function injectHeader(): void {
            const overlaysList = (layersControl as unknown as {_overlaysList?: HTMLElement} | null)?._overlaysList;

            if (!overlaysList) {
                return;
            }

            const firstFloodLabel = Array.from(overlaysList.querySelectorAll('label')).find((label) => {
                const input = label.querySelector('input') as (HTMLInputElement & {layerId?: number}) | null;
                return input?.layerId !== undefined && overlayIds.has(input.layerId);
            });

            if (firstFloodLabel) {
                const header = document.createElement('div');
                header.className = HEADER_CLASS;
                header.textContent = SECTION_TITLE;
                overlaysList.insertBefore(header, firstFloodLabel);
            }
        }

        // The layers control rebuilds its list on every change, so the header is placed again after each.
        function patchLayersControlUpdate(): void {
            const control = layersControl as unknown as {_update?: () => unknown} | null;
            if (!control?._update) {
                return;
            }
            const original = control._update;
            originalUpdate = original;
            control._update = (): unknown => {
                const result = original.call(layersControl);
                injectHeader();
                return result;
            };
        }

        function restoreLayersControlUpdate(): void {
            if (layersControl && originalUpdate) {
                (layersControl as unknown as {_update?: () => unknown})._update = originalUpdate;
                originalUpdate = null;
            }
        }

        function initLayers(): void {
            if (!map || availableScenarios.length === 0) {
                return;
            }

            const pane = map.createPane(PANE_NAME);
            pane.style.zIndex = PANE_Z_INDEX;

            for (const scenario of availableScenarios) {
                const overlay = L.geoJSON(undefined, {
                    pane: PANE_NAME,
                    style: (feature) => areaStyle(feature?.properties as FloodZoneArea),
                });
                overlays.set(scenario, overlay);
                overlayIds.add(L.Util.stamp(overlay));

                if (layersControl) {
                    layersControl.addOverlay(overlay, overlayLabel(scenario));
                    registerMinZoom(map, layersControl, overlay, MIN_ZOOM_BY_SCENARIO[scenario]);
                }
            }

            legend = buildLegend();
            zoomHint = buildZoomHint();

            patchLayersControlUpdate();
            injectHeader();

            map.on('moveend', onMapChange);
            map.on('overlayadd', onMapChange);
            map.on('overlayremove', onMapChange);
            map.on('click', onMapClick);
            map.on('popupopen', onPopupOpen);

            for (const scenario of props.initialScenarios) {
                const overlay = overlays.get(scenario);
                if (overlay) {
                    map.addLayer(overlay);
                }
            }

            if (props.focus !== null) {
                const place = L.latLng(props.focus.latitude, props.focus.longitude);
                focusMarker = L.circleMarker(place, FOCUS_MARKER_STYLE).addTo(map);
                whenSized(map, () => void showPlace(place));
            }

            onMapChange();
        }

        function detachFromMap(): void {
            if (map) {
                restoreLayersControlUpdate();
                map.off('moveend', onMapChange);
                map.off('overlayadd', onMapChange);
                map.off('overlayremove', onMapChange);
                map.off('click', onMapClick);
                map.off('popupopen', onPopupOpen);
                abortController?.abort();

                if (layersControl) {
                    unregisterMinZooms(layersControl, Array.from(overlays.values()));
                }

                for (const overlay of overlays.values()) {
                    map.removeLayer(overlay);
                }

                if (focusMarker) {
                    map.removeLayer(focusMarker);
                }

                legend?.remove();
                zoomHint?.remove();
            }

            focusMarker = null;
            lastOpenedPopup = null;

            overlays.clear();
            overlayIds.clear();
            legend = null;
            zoomHint = null;
            loadedBounds = null;
            loadedBand = null;
            loadedScenarios = '';
            layersControl = null;
            map = null;
        }

        function attachToMap(acquired: AcquiredMap): void {
            map = acquired.map;
            layersControl = acquired.layersControl;
            initLayers();
        }

        onMounted(() => {
            cleanupMapListener = acquireLeafletMap(rootElement.value!, attachToMap);
        });

        onBeforeUnmount(() => {
            cleanupMapListener?.();
            detachFromMap();
        });

        return {rootElement};
    },
});
</script>
